import { Box } from '@web/components/atoms/Layout'
import { PageComponentProps } from '../utils'
import React, { useMemo } from 'react'
import { ComponentType, PageComponent } from '@web/graphql-queries/graphql'
import ErrorBoundary from '@web/components/molecules/ErrorBoundary'
import { Body2 } from '@web/components/atoms/Typography'
import { useMediaQuery } from '@web/components/utils/chakra-hooks'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const BaseComponent = (
  props: PageComponentProps & {
    component: PageComponent | null | undefined
    element: any
    errorBoundaryProps?: {
      apiPrefix?: string
      httpClient?: (<T>(url: string, options?: RequestInit | undefined) => Promise<T>) | undefined
    }
  },
) => {
  const component = props.component
  const styles = component?.styles ?? {}
  const mobileStyles = component?.mobileStyles ?? {}
  const [isMobile] = useMediaQuery(['(max-width: 900px)'], { fallback: [] })

  const hasVisibleComponents = useMemo(() => {
    if (
      !isMobile ||
      props.component?.type !== ComponentType.Row ||
      !props.childComponents ||
      props.childComponents.length === 0
    ) {
      return true
    }

    const components = props.allComponents

    return (
      (
        props.childComponents
          .flat()
          .map((c) => c?.childComponents?.flat())
          .flat()
          .filter((c) => c && components?.get(c.id)?.mobileStyles?.display !== 'none')
          .map((c) => c?.id) ?? []
      ).length > 0
    )
  }, [isMobile])

  if (!component || !hasVisibleComponents) {
    return null
  }

  if (typeof props.element === 'undefined') {
    console.error(`Component of type ${component.type} was not found and will not be rendered"`)
    return null
  }

  const getWidth = (width: string | null, margin: string | null) => {
    const widthToUse = props.isWidthSet ? '100%' : width

    if (!margin) {
      return widthToUse
    }

    const marginValues = margin.split(' ')

    return `calc(${widthToUse} - ${marginValues[1].includes('-') ? '0px' : marginValues[1]} - ${
      marginValues[3].includes('-') ? '0px' : marginValues[3]
    })`
  }

  return (
    <ErrorBoundary
      {...props.errorBoundaryProps}
      fallback={
        <Body2 color="ge.text.danger">
          Something went wrong rendering this component. Please refresh the page and try again.
        </Body2>
      }
    >
      <Box
        {...(styles?.id ? { id: styles.id } : {})}
        {...(styles?.class ? { className: styles.class } : {})}
        {...(isMobile && mobileStyles?.id ? { id: mobileStyles.id } : {})}
        {...(isMobile && mobileStyles?.class ? { className: mobileStyles.class } : {})}
        {...(styles?.scrollBehavior === 'sticky' ? { position: 'sticky', top: '24px' } : { position: 'relative' })}
        {...(styles?.margin?.includes('-') ? { zIndex: 1 } : {})}
        css={{
          display: mobileStyles?.display ?? 'flex',
          flexDirection: 'column',
          alignSelf: mobileStyles?.alignSelf,
          alignItems: 'inherit',
          height:
            mobileStyles?.height && mobileStyles?.height !== 'auto' && !mobileStyles?.margin?.includes('-')
              ? '100%'
              : 'auto',
          minHeight: mobileStyles?.height === '100%' ? 'inherit' : mobileStyles?.height,
          margin: mobileStyles?.margin,
          width: getWidth(mobileStyles?.width, mobileStyles?.margin) ?? '100%',
          '@media (min-width: 901px)': {
            alignSelf: styles?.alignSelf,
            display: styles?.display ?? 'block',
            height:
              styles?.height &&
              styles?.height !== 'auto' &&
              component.type === ComponentType.Column &&
              !styles?.margin?.includes('-')
                ? '100%'
                : 'auto',
            minHeight: styles?.height === '100%' || !styles?.height ? 'inherit' : styles?.height,
            margin: styles?.margin ?? '0px', // this shouldn't get overriden by mobile
            width: getWidth(styles?.width, styles?.margin) ?? '100%',
          },
        }}
      >
        {React.createElement(props.element, {
          key: `${props.id}-component`,
          ...component,
          ...props,
        })}
      </Box>
    </ErrorBoundary>
  )
}

export default BaseComponent
